import { Button, Container, Text } from "@mantine/core";
import { IconPlus } from "@tabler/icons-react";
import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { modals } from "@mantine/modals";
import { notifications } from "@mantine/notifications";
import EmptyState from "../components/common/EmptyState";
import LoadingSpinner from "../components/common/LoadingSpinner";
import PageHeader from "../components/common/PageHeader";
import Pagination from "../components/common/Pagination";
import UserFilters from "../components/users/UserFilters";
import UserModal, { type UserFormData } from "../components/users/UserModal";
import UserOverview from "../components/users/UserOverview";
import UserTable, {
	type UserSortField,
	type SortDirection,
} from "../components/users/UserTable";
import {
	fetchUsers,
	fetchUserStats,
	fetchFilteredUserStats,
	clearFilteredStats,
	createUser,
	updateUser,
	deleteUser,
	clearError,
	setPage,
	type User,
} from "../store/users/usersSlice";
import type { AppDispatch, RootState } from "../store/store";

function UsersPage() {
	const dispatch = useDispatch<AppDispatch>();
	const { users, loading, error, total, page, limit, stats, filteredStats } =
		useSelector((state: RootState) => state.users);
	const { user: currentUser } = useSelector((state: RootState) => state.auth);

	const [modalOpened, setModalOpened] = useState(false);
	const [editingUser, setEditingUser] = useState<User | null>(null);
	const [searchTerm, setSearchTerm] = useState("");
	const [roleFilter, setRoleFilter] = useState<string>("");
	const [sortField, setSortField] = useState<UserSortField>(null);
	const [sortDirection, setSortDirection] = useState<SortDirection>(null);
	const [refreshKey, setRefreshKey] = useState(0);

	// UserFilters also reports the search when it mounts; resetting the page
	// then would throw away the page kept in the store across visits.
	const handleSearchChange = useCallback(
		(value: string) => {
			if (value === searchTerm) return;
			setSearchTerm(value);
			dispatch(setPage(1));
		},
		[dispatch, searchTerm]
	);

	const handleRoleFilterChange = (value: string) => {
		setRoleFilter(value);
		dispatch(setPage(1));
	};

	// Sorting is applied by the API over the whole result set. Reordering the
	// rows here would only ever reach the ten already on screen.
	const handleSort = (field: UserSortField) => {
		if (sortField === field) {
			if (sortDirection === "desc") {
				setSortDirection("asc");
			} else if (sortDirection === "asc") {
				setSortField(null);
				setSortDirection(null);
			} else {
				setSortDirection("desc");
			}
		} else {
			setSortField(field);
			setSortDirection("desc");
		}
		dispatch(setPage(1));
	};

	const totalPages = Math.ceil(total / limit);

	// Refetching through the effect below rather than dispatching here keeps one
	// source of the page number: a delete can shrink the list under the current
	// page while the clamp below is moving it back.
	const refreshData = () => {
		setRefreshKey((key) => key + 1);
		dispatch(fetchUserStats());
	};

	useEffect(() => {
		dispatch(fetchUserStats());
	}, [dispatch]);

	// Filtered counts sit under each Overview total, over exactly what the
	// table shows. refreshKey: an edit can move users in or out of the filter.
	const hasFilters = Boolean(searchTerm || roleFilter);
	useEffect(() => {
		if (!hasFilters) {
			dispatch(clearFilteredStats());
			return;
		}
		dispatch(
			fetchFilteredUserStats({
				search: searchTerm || undefined,
				role: roleFilter || undefined,
			}),
		);
	}, [dispatch, hasFilters, searchTerm, roleFilter, refreshKey]);

	const filterLabel = [
		roleFilter && roleFilter.charAt(0).toUpperCase() + roleFilter.slice(1),
		searchTerm && `"${searchTerm}"`,
	]
		.filter(Boolean)
		.join(" · ");

	useEffect(() => {
		dispatch(
			fetchUsers({
				page,
				limit,
				search: searchTerm,
				role: roleFilter || undefined,
				sortBy: sortField ?? undefined,
				sortOrder: sortDirection ?? undefined,
			})
		);
	}, [
		dispatch,
		page,
		limit,
		searchTerm,
		roleFilter,
		sortField,
		sortDirection,
		refreshKey,
	]);

	// Deleting the only row on the last page left the page past the end, which
	// rendered as "no users found" with no pagination to get back with.
	useEffect(() => {
		if (totalPages > 0 && page > totalPages) {
			dispatch(setPage(totalPages));
		}
	}, [dispatch, page, totalPages]);

	useEffect(() => {
		if (error) {
			notifications.show({
				title: "Error",
				message: error,
				color: "red",
			});
			dispatch(clearError());
		}
	}, [error, dispatch]);

	const handleCreateUser = async (values: UserFormData) => {
		try {
			if (!values.password) {
				notifications.show({
					title: "Error",
					message: "Password is required for new users",
					color: "red",
				});
				return;
			}

			const createData = {
				username: values.username,
				email: values.email,
				password: values.password,
				role: values.role,
			};

			await dispatch(createUser(createData)).unwrap();
			setModalOpened(false);
			refreshData();
			notifications.show({
				title: "Success",
				message: "User created successfully",
				color: "green",
			});
		} catch {
			// Error handled by Redux
		}
	};

	const handleUpdateUser = async (values: UserFormData) => {
		if (!editingUser) return;

		try {
			const updateData = values.password ? values : { ...values };
			delete updateData.password;
			await dispatch(
				updateUser({
					id: editingUser._id,
					...updateData,
				})
			).unwrap();
			setModalOpened(false);
			setEditingUser(null);
			refreshData();
			notifications.show({
				title: "Success",
				message: "User updated successfully",
				color: "green",
			});
		} catch {
			// Error handled by Redux
		}
	};

	const handleToggleUser = async (user: User) => {
		if (user._id === currentUser?.id) {
			notifications.show({
				title: "Error",
				message: "You cannot deactivate your own account",
				color: "red",
			});
			return;
		}

		try {
			await dispatch(
				updateUser({
					id: user._id,
					username: user.username,
					email: user.email,
					role: user.role,
					isActive: !user.isActive,
				})
			).unwrap();
			refreshData();
			notifications.show({
				title: "Success",
				message: `User ${
					user.isActive ? "deactivated" : "activated"
				} successfully`,
				color: "green",
			});
		} catch {
			// Error handled by Redux
		}
	};

	const handleDeleteUser = (id: string, username: string) => {
		if (id === currentUser?.id) {
			notifications.show({
				title: "Error",
				message: "You cannot delete your own account",
				color: "red",
			});
			return;
		}

		modals.openConfirmModal({
			title: "Delete User",
			children: (
				<Text>
					Are you sure you want to delete the user "{username}"? This action
					cannot be undone.
				</Text>
			),
			labels: { confirm: "Delete", cancel: "Cancel" },
			confirmProps: { color: "red" },
			onConfirm: async () => {
				try {
					await dispatch(deleteUser(id)).unwrap();
					refreshData();
					notifications.show({
						title: "Success",
						message: "User deleted successfully",
						color: "green",
					});
				} catch {
					// Error handled by Redux
				}
			},
		});
	};

	const openModal = (user?: User) => {
		setEditingUser(user || null);
		setModalOpened(true);
	};

	const pageActions = currentUser?.role === "admin" && (
		<Button
			leftSection={<IconPlus size={16} />}
			onClick={() => openModal()}>
			Create User
		</Button>
	);

	return (
		<Container size="xl">
			<PageHeader
				title="Users"
				description="Manage system users and permissions"
				actions={pageActions}
			/>

			<UserOverview
				total={stats.total}
				active={stats.active}
				admins={stats.admins}
				managers={stats.managers}
				filtered={hasFilters ? filteredStats : null}
				filterLabel={filterLabel}
			/>

			<UserFilters
				searchTerm={searchTerm}
				roleFilter={roleFilter}
				onSearchChange={handleSearchChange}
				onRoleFilterChange={handleRoleFilterChange}
			/>

			{loading ? (
				<LoadingSpinner />
			) : users.length === 0 ? (
				<EmptyState
					title="No users found"
					message={
						searchTerm || roleFilter
							? "No users match your search criteria."
							: "No users have been created yet. Create your first user to get started."
					}
				/>
			) : (
				<UserTable
					users={users}
					currentUserId={currentUser?.id}
					currentUserRole={currentUser?.role}
					sortField={sortField}
					sortDirection={sortDirection}
					onSort={handleSort}
					onToggleUser={handleToggleUser}
					onEditUser={openModal}
					onDeleteUser={handleDeleteUser}
				/>
			)}

			{/* Outside the empty branch: a page past the end renders empty, and
			    the controls are the way back from it. */}
			<Pagination
				currentPage={page}
				totalPages={totalPages}
				onPageChange={(newPage) => dispatch(setPage(newPage))}
			/>

			<UserModal
				opened={modalOpened}
				user={editingUser}
				loading={loading}
				onClose={() => setModalOpened(false)}
				onSubmit={editingUser ? handleUpdateUser : handleCreateUser}
			/>
		</Container>
	);
}

export default UsersPage;
